import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumVesselAvailability } from "./enums/enumVesselAvailability.generated";
import { EnumVesselMainPurpose } from "./enums/enumVesselMainPurpose.generated";

// Model Objects

export class ProductionVessel extends ModelObject<ProductionVessel> {

    public vesselTypeId: Guid = Guid.createEmpty();
    /**
    * Display Colour
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public displayColour: string = "";
    /**
    * The order for this vessel
    */
    public ordering: number = 0;
    /**
    * Id of the vessel.
    */
    public vesselId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselTypeId = Guid.parseOrEmpty(jsonSerializedObject.vesselTypeId);
          this.displayColour = jsonSerializedObject.displayColour;
          this.ordering = jsonSerializedObject.ordering;
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselTypeId = this.vesselTypeId.toString();
        toRet.displayColour = this.displayColour;
        toRet.ordering = this.ordering;
        toRet.vesselId = this.vesselId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselTypeId", rule: (v: any) => !v.isEmpty() || "ProductionVessel.VesselTypeId cannot be empty"},
            {field: "ordering", rule: (v: any) => v == null || v >= -2147483648 || "Ordering is not greater than minimum allowed value: -2147483648"},
            {field: "ordering", rule: (v: any) => v == null || v <= 2147483647 || "Ordering is above maximum allowed value: 2147483647"},
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "ProductionVessel.VesselId cannot be empty"},
    ];
}


export class CaskVessel extends ModelObject<CaskVessel> {

    /**
    * Id of the vessel.
    */
    public vesselId: Guid = Guid.createEmpty();
    public code: string = "";
    private _caskSubLocationId: Guid | null = null;
    public get caskSubLocationId(): Guid | null { return this._caskSubLocationId }
    public set caskSubLocationId(value: Guid | null) {
      this._caskSubLocationId = value === undefined ? null : value;
    }
    private _caskSubSubLocationId: Guid | null = null;
    public get caskSubSubLocationId(): Guid | null { return this._caskSubSubLocationId }
    public set caskSubSubLocationId(value: Guid | null) {
      this._caskSubSubLocationId = value === undefined ? null : value;
    }
    private _caskTypeId: Guid | null = null;
    public get caskTypeId(): Guid | null { return this._caskTypeId }
    public set caskTypeId(value: Guid | null) {
      this._caskTypeId = value === undefined ? null : value;
    }
    private _caskSubTypeId: Guid | null = null;
    public get caskSubTypeId(): Guid | null { return this._caskSubTypeId }
    public set caskSubTypeId(value: Guid | null) {
      this._caskSubTypeId = value === undefined ? null : value;
    }
    public owner: string = "";
    private _purchasedDate: LocalDate | null = null;
    public get purchasedDate(): LocalDate | null { return this._purchasedDate }
    public set purchasedDate(value: LocalDate | null) {
      this._purchasedDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.code = jsonSerializedObject.code;
          this.caskSubLocationId = jsonSerializedObject.caskSubLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.caskSubLocationId);
          this.caskSubSubLocationId = jsonSerializedObject.caskSubSubLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.caskSubSubLocationId);
          this.caskTypeId = jsonSerializedObject.caskTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.caskTypeId);
          this.caskSubTypeId = jsonSerializedObject.caskSubTypeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.caskSubTypeId);
          this.owner = jsonSerializedObject.owner;
          this.purchasedDate = jsonSerializedObject.purchasedDate == null ? null : LocalDate.parse(jsonSerializedObject.purchasedDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.code = this.code;
        toRet.caskSubLocationId = this.caskSubLocationId == null ? null : this.caskSubLocationId.toString();
        toRet.caskSubSubLocationId = this.caskSubSubLocationId == null ? null : this.caskSubSubLocationId.toString();
        toRet.caskTypeId = this.caskTypeId == null ? null : this.caskTypeId.toString();
        toRet.caskSubTypeId = this.caskSubTypeId == null ? null : this.caskSubTypeId.toString();
        toRet.owner = this.owner;
        toRet.purchasedDate = this.purchasedDate == null ? null : this.purchasedDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "CaskVessel.VesselId cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "caskSubLocationId", rule: (v: any) => v == null || !v.isEmpty() || "CaskVessel.CaskSubLocationId cannot be empty"},
            {field: "caskSubSubLocationId", rule: (v: any) => v == null || !v.isEmpty() || "CaskVessel.CaskSubSubLocationId cannot be empty"},
            {field: "caskTypeId", rule: (v: any) => v == null || !v.isEmpty() || "CaskVessel.CaskTypeId cannot be empty"},
            {field: "caskSubTypeId", rule: (v: any) => v == null || !v.isEmpty() || "CaskVessel.CaskSubTypeId cannot be empty"},
            {field: "owner", rule: (v: any) => v == null || v.length <= 1000 || "Owner has too many characters (1000)"},
    ];
}


export class VesselReadOnly extends ModelObject<VesselReadOnly> {

    /**
    * Timestamp at which the vessel was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * User who created the vessel. Captured by system not user editable.
    */
    public createdBy: Guid = Guid.createEmpty();
    private _currentAssemblyId: Guid | null = null;
    /**
    * The process currently in control of this vessel. Vessel should either be in an Assembly or Process, but not both.
    */
    public get currentAssemblyId(): Guid | null { return this._currentAssemblyId }
    public set currentAssemblyId(value: Guid | null) {
      this._currentAssemblyId = value === undefined ? null : value;
    }
    private _currentProcessId: Guid | null = null;
    /**
    * The process currently in control of this vessel. Vessel should either be in an Assembly or Process, but not both.
    */
    public get currentProcessId(): Guid | null { return this._currentProcessId }
    public set currentProcessId(value: Guid | null) {
      this._currentProcessId = value === undefined ? null : value;
    }
    /**
    * The Use Number for this vessel
    */
    public useNumber: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.createdBy = Guid.parseOrEmpty(jsonSerializedObject.createdBy);
          this.currentAssemblyId = jsonSerializedObject.currentAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.currentAssemblyId);
          this.currentProcessId = jsonSerializedObject.currentProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.currentProcessId);
          this.useNumber = jsonSerializedObject.useNumber;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdAt = this.createdAt.toString();
        toRet.createdBy = this.createdBy.toString();
        toRet.currentAssemblyId = this.currentAssemblyId == null ? null : this.currentAssemblyId.toString();
        toRet.currentProcessId = this.currentProcessId == null ? null : this.currentProcessId.toString();
        toRet.useNumber = this.useNumber;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdBy", rule: (v: any) => !v.isEmpty() || "VesselReadOnly.CreatedBy cannot be empty"},
            {field: "currentAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "VesselReadOnly.CurrentAssemblyId cannot be empty"},
            {field: "currentProcessId", rule: (v: any) => v == null || !v.isEmpty() || "VesselReadOnly.CurrentProcessId cannot be empty"},
            {field: "useNumber", rule: (v: any) => v == null || v >= -2147483648 || "UseNumber is not greater than minimum allowed value: -2147483648"},
            {field: "useNumber", rule: (v: any) => v == null || v <= 2147483647 || "UseNumber is above maximum allowed value: 2147483647"},
    ];
}


export class Vessel extends BaseModelObject<Vessel> {

    public id: Guid = Guid.createEmpty();
    public isHidden: boolean = false;
    public version: number = 0;
    /**
    * Capacity of Vessel in Litres
    * Not Negative
    */
    public capacityLitres: number = 0;
    /**
    * Main purpose of the vessel. Used to link the secondary table to this Vessel.
    */
    public mainPurpose: EnumVesselMainPurpose = EnumVesselMainPurpose.unknown;
    /**
    * Display name for this Vessel
    * Not Nullable
    * Not Empty
    * Preserves leading and trailing whitespce.
    */
    public name: string = "";
    /**
    * Shortened display name for this Vessel
    * Not Nullable
    * Max Length : 1000
    * Preserves leading and trailing whitespce.
    */
    public shortName: string = "";
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    * Preserves leading and trailing whitespce.
    */
    public notes: string = "";
    /**
    * The Location for this Vessel
    */
    public stockLocationId: Guid = Guid.createEmpty();
    private _supplierId: Guid | null = null;
    /**
    * The Supplier for this Vessel
    */
    public get supplierId(): Guid | null { return this._supplierId }
    public set supplierId(value: Guid | null) {
      this._supplierId = value === undefined ? null : value;
    }
    private _vesselStateId: Guid | null = null;
    /**
    * The current state of the vessel
    */
    public get vesselStateId(): Guid | null { return this._vesselStateId }
    public set vesselStateId(value: Guid | null) {
      this._vesselStateId = value === undefined ? null : value;
    }
    private _weightKg: number | null = null;
    /**
    * Weight of this vessel.
    * Not Negative
    */
    public get weightKg(): number | null { return this._weightKg }
    public set weightKg(value: number | null) {
      this._weightKg = value === undefined ? null : value;
    }
    public readOnly: VesselReadOnly = new VesselReadOnly();
    private _productionVessel: ProductionVessel | null = null;
    public get productionVessel(): ProductionVessel | null { return this._productionVessel }
    public set productionVessel(value: ProductionVessel | null) {
      this._productionVessel = value === undefined ? null : value;
    }
    private _caskVessel: CaskVessel | null = null;
    public get caskVessel(): CaskVessel | null { return this._caskVessel }
    public set caskVessel(value: CaskVessel | null) {
      this._caskVessel = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.isHidden = jsonSerializedObject.isHidden;
          this.version = jsonSerializedObject.version;
          this.capacityLitres = jsonSerializedObject.capacityLitres;
          this.mainPurpose = EnumVesselMainPurpose.getByValue(jsonSerializedObject.mainPurpose, this.mainPurpose);
          this.name = jsonSerializedObject.name;
          this.shortName = jsonSerializedObject.shortName;
          this.notes = jsonSerializedObject.notes;
          this.stockLocationId = Guid.parseOrEmpty(jsonSerializedObject.stockLocationId);
          this.supplierId = jsonSerializedObject.supplierId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.supplierId);
          this.vesselStateId = jsonSerializedObject.vesselStateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.vesselStateId);
          this.weightKg = jsonSerializedObject.weightKg == null ? null : jsonSerializedObject.weightKg;
          this.readOnly = new VesselReadOnly(jsonSerializedObject.readOnly);
          this.productionVessel = jsonSerializedObject.productionVessel == null ? null : new ProductionVessel(jsonSerializedObject.productionVessel);
          this.caskVessel = jsonSerializedObject.caskVessel == null ? null : new CaskVessel(jsonSerializedObject.caskVessel);
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.isHidden = this.isHidden;
        toRet.version = this.version;
        toRet.capacityLitres = this.capacityLitres;
        toRet.mainPurpose = this.mainPurpose.value;
        toRet.name = this.name;
        toRet.shortName = this.shortName;
        toRet.notes = this.notes;
        toRet.stockLocationId = this.stockLocationId.toString();
        toRet.supplierId = this.supplierId == null ? null : this.supplierId.toString();
        toRet.vesselStateId = this.vesselStateId == null ? null : this.vesselStateId.toString();
        toRet.weightKg = this.weightKg == null ? null : this.weightKg;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.productionVessel = this.productionVessel == null ? null : this.productionVessel?.toJsonSerializedObject() ?? null;
        toRet.caskVessel = this.caskVessel == null ? null : this.caskVessel?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "Vessel.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "shortName", rule: (v: any) => v == null || v.length <= 1000 || "ShortName has too many characters (1000)"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "stockLocationId", rule: (v: any) => !v.isEmpty() || "Vessel.StockLocationId cannot be empty"},
            {field: "supplierId", rule: (v: any) => v == null || !v.isEmpty() || "Vessel.SupplierId cannot be empty"},
            {field: "vesselStateId", rule: (v: any) => v == null || !v.isEmpty() || "Vessel.VesselStateId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "productionVessel", rule: (v: any) => (v === null) || v.validate()},
            {field: "caskVessel", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class VesselAvailabilityState extends ModelObject<VesselAvailabilityState> {

    public vesselId: Guid = Guid.createEmpty();
    public availability: EnumVesselAvailability = EnumVesselAvailability.available;
    public requiredMaintenanceRecipeId: Guid[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.availability = EnumVesselAvailability.getByValue(jsonSerializedObject.availability, this.availability);
          this.requiredMaintenanceRecipeId = jsonSerializedObject.requiredMaintenanceRecipeId.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.availability = this.availability.value;
        toRet.requiredMaintenanceRecipeId = this.requiredMaintenanceRecipeId.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselAvailabilityState.VesselId cannot be empty"},
    ];
}


export class VesselCalculatedDetail extends ModelObject<VesselCalculatedDetail> {

    public vesselId: Guid = Guid.createEmpty();
    private _currentAssemblyFillDate: LocalDate | null = null;
    /**
    * Vessel activity first fill date
    */
    public get currentAssemblyFillDate(): LocalDate | null { return this._currentAssemblyFillDate }
    public set currentAssemblyFillDate(value: LocalDate | null) {
      this._currentAssemblyFillDate = value === undefined ? null : value;
    }
    /**
    * Current assembly/process number
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public number: string = "";
    private _currentAbv: number | null = null;
    /**
    * From the latest QC test measurement of type ABV. Null 
    */
    public get currentAbv(): number | null { return this._currentAbv }
    public set currentAbv(value: number | null) {
      this._currentAbv = value === undefined ? null : value;
    }
    /**
    * Current assembly/process batch number. Empty if not yet defined.
    * Not Nullable
    * Preserves leading and trailing whitespce.
    */
    public batchNumber: string = "";
    private _contents: Guid | null = null;
    /**
    * Current assembly/process output produced. Null if not yet defined.
    */
    public get contents(): Guid | null { return this._contents }
    public set contents(value: Guid | null) {
      this._contents = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.currentAssemblyFillDate = jsonSerializedObject.currentAssemblyFillDate == null ? null : LocalDate.parse(jsonSerializedObject.currentAssemblyFillDate);
          this.number = jsonSerializedObject.number;
          this.currentAbv = jsonSerializedObject.currentAbv == null ? null : jsonSerializedObject.currentAbv;
          this.batchNumber = jsonSerializedObject.batchNumber;
          this.contents = jsonSerializedObject.contents == null ? null : Guid.parseOrEmpty(jsonSerializedObject.contents);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.currentAssemblyFillDate = this.currentAssemblyFillDate == null ? null : this.currentAssemblyFillDate.toString();
        toRet.number = this.number;
        toRet.currentAbv = this.currentAbv == null ? null : this.currentAbv;
        toRet.batchNumber = this.batchNumber;
        toRet.contents = this.contents == null ? null : this.contents.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselCalculatedDetail.VesselId cannot be empty"},
            {field: "contents", rule: (v: any) => v == null || !v.isEmpty() || "VesselCalculatedDetail.Contents cannot be empty"},
    ];
}


export class VesselFilter extends ModelObject<VesselFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _limitMainPurpose: EnumVesselMainPurpose[] | null = null;
    public get limitMainPurpose(): EnumVesselMainPurpose[] | null { return this._limitMainPurpose }
    public set limitMainPurpose(value: EnumVesselMainPurpose[] | null) {
      this._limitMainPurpose = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.limitMainPurpose = jsonSerializedObject.limitMainPurpose == null ? null : jsonSerializedObject.limitMainPurpose.map((v: any) => EnumVesselMainPurpose.getByValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.limitMainPurpose = this.limitMainPurpose == null ? null : this.limitMainPurpose.map(v => v.value);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredVesselResults extends ModelObject<FilteredVesselResults> {

    public matchingCount: number = 0;
    public vessels: Vessel[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.vessels = jsonSerializedObject.vessels.map((v: any) => new Vessel(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.vessels = this.vessels.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "vessels", rule: (v: any) => !!v || "vessels is required"},
            {field: "vessels", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as Vessel[]).reduce((acc: Array<string>, curr: Vessel) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class VesselValuation extends ModelObject<VesselValuation> {

    public vesselId: Guid = Guid.createEmpty();
    private _value: number | null = null;
    public get value(): number | null { return this._value }
    public set value(value: number | null) {
      this._value = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.value = jsonSerializedObject.value == null ? null : jsonSerializedObject.value;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.value = this.value == null ? null : this.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselValuation.VesselId cannot be empty"},
    ];
}


export class VesselService extends ServiceBase {

  public async getVessel(tenantId: Guid, vesselId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel> {
    const req = {
    tenantId:tenantId.toString(),
    vesselId:vesselId.toString(),
    };
    const extractor = (response:any) => new Vessel(response.vessel);;
    return this.makeJsonRequest("vessel/v2/getVessel", req, false, extractor, opts);
  };

  public async getVessels(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getVessels", req, false, extractor, opts);
  };

  public async getAllVessels(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getAllVessels", req, false, extractor, opts);
  };

  public async saveVessel(tenantId: Guid, vessel: Vessel, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    vessel:vessel.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/saveVessel", req, true, extractor, opts);
  };

  public async updateProductionVesselOrdering(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/updateProductionVesselOrdering", req, true, extractor, opts);
  };

  public async getVesselsLockedByAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getVesselsLockedByAssembly", req, false, extractor, opts);
  };

  public async lockVesselsForAssembly(tenantId: Guid, assemblyId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/lockVesselsForAssembly", req, true, extractor, opts);
  };

  public async areVesselsAvailable(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselAvailabilityState[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.availabilities.map((v: any) => new VesselAvailabilityState(v));;
    return this.makeJsonRequest("vessel/v2/areVesselsAvailable", req, false, extractor, opts);
  };

  public async releaseVesselsFromAssembly(tenantId: Guid, assemblyId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/releaseVesselsFromAssembly", req, true, extractor, opts);
  };

  public async transferVesselLocksBetweenAssemblies(tenantId: Guid, sourceAssemblyId: Guid, targetAssemblyId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    sourceAssemblyId:sourceAssemblyId.toString(),
    targetAssemblyId:targetAssemblyId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/transferVesselLocksBetweenAssemblies", req, true, extractor, opts);
  };

  public async setVesselIsHidden(tenantId: Guid, vesselId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    vesselId:vesselId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/setVesselIsHidden", req, true, extractor, opts);
  };

  public async getVesselCalculatedDetails(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselCalculatedDetail[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselCalculatedDetails.map((v: any) => new VesselCalculatedDetail(v));;
    return this.makeJsonRequest("vessel/v2/getVesselCalculatedDetails", req, false, extractor, opts);
  };

  public async getVesselsByFilter(tenantId: Guid, filter: VesselFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredVesselResults> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredVesselResults(response.results);;
    return this.makeJsonRequest("vessel/v2/getVesselsByFilter", req, false, extractor, opts);
  };

  public async getVesselsLockedByProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getVesselsLockedByProcess", req, false, extractor, opts);
  };

  public async lockVesselsForProcess(tenantId: Guid, processId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/lockVesselsForProcess", req, true, extractor, opts);
  };

  public async releaseVesselsFromProcess(tenantId: Guid, processId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/releaseVesselsFromProcess", req, true, extractor, opts);
  };

  public async transferVesselLocksBetweenProcesses(tenantId: Guid, sourceProcessId: Guid, targetProcessId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    sourceProcessId:sourceProcessId.toString(),
    targetProcessId:targetProcessId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("vessel/v2/transferVesselLocksBetweenProcesses", req, true, extractor, opts);
  };

  public async getVesselsLockedByProcesses(tenantId: Guid, processIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    processIds:processIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getVesselsLockedByProcesses", req, false, extractor, opts);
  };

  public async getVesselsLockedByAssemblies(tenantId: Guid, assemblyIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Vessel[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyIds:assemblyIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vessels.map((v: any) => new Vessel(v));;
    return this.makeJsonRequest("vessel/v2/getVesselsLockedByAssemblies", req, false, extractor, opts);
  };

  public async getVesselsCurrentValue(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselValuation[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselsValuation.map((v: any) => new VesselValuation(v));;
    return this.makeJsonRequest("vessel/v2/getVesselsCurrentValue", req, false, extractor, opts);
  };
}

export const vesselService = new VesselService();